import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, CircularProgress,
  Chip, Tabs, Tab, Box, Paper, Tooltip, Alert, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import { apiPaths, postInstituteAppData, getInstutueApp, putInstituteAppData, patchInstituteAppData, deleteInstituteAppData } from '../api-integration-util';
import { getUserDetails } from '../utils/shared-data.service';
import { usePermission } from '../utils/PermissionGate';

const STYLE_TAGS = [
  { key: 'CLINICAL_VIGNETTE', label: 'Clinical Scenario', color: '#3366FF' },
  { key: 'CROSS_SUBJECT', label: 'Cross-Subject Integration', color: '#7c3aed' },
  { key: 'NEXT_BEST_STEP', label: 'Next Best Step', color: '#22c55e' },
  { key: 'EXCEPT_NOT', label: 'Except / NOT Type', color: '#dc3545' },
  { key: 'ONE_LINER', label: 'One-Liner Factual', color: '#f59e0b' },
  { key: 'DRUG_IOC', label: 'Drug / Investigation of Choice', color: '#06b6d4' },
  { key: 'IMAGE_DESCRIPTION', label: 'Image-Based (Descriptive)', color: '#ea580c' },
  { key: 'MATCH_FOLLOWING', label: 'Match the Following', color: '#8492a6' },
];

const joditConfig = {
  readonly: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  height: 250,
  buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
};

const getHeaders = () => ({
  tenantId: getUserDetails().tenantId,
  userEmail: getUserDetails().email,
});

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ─── Variation Card (shared by both tabs) ──────────────────
function VariationCard({ variation, mode, onApply, onEdit, onReject, onDelete, applying }) {
  const canApply = usePermission('QA Actions', mode === 'restyle' ? 'Apply Restyle' : 'Insert Generated');
  const canUpdate = usePermission('QA Actions', 'Update Variation');
  const canReject = usePermission('QA Actions', 'Reject Variation');
  const canDelete = usePermission('QA Actions', 'Delete Variation');
  const [editing, setEditing] = useState(false);
  const [editQuestion, setEditQuestion] = useState('');
  const [editExplanation, setEditExplanation] = useState('');
  const [saving, setSaving] = useState(false);
  const questionEditorRef = useRef(null);
  const explanationEditorRef = useRef(null);

  const questionField = mode === 'restyle' ? 'restyledQuestion' : 'generatedQuestion';
  const explanationField = mode === 'restyle' ? 'restyledExplanation' : 'generatedExplanation';
  const questionText = variation[questionField];
  const explanationText = variation[explanationField];

  const handleStartEdit = () => {
    setEditQuestion(questionText || '');
    setEditExplanation(explanationText || '');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await onEdit(variation.id, editQuestion, editExplanation);
      variation[questionField] = editQuestion;
      variation[explanationField] = editExplanation;
      setEditing(false);
      toast.success('Variation updated');
    } catch (e) {
      toast.error('Failed to save edit');
    } finally {
      setSaving(false);
    }
  };

  const isPending = variation.status === 'pending';

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
          Variation {variation.variationIndex}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip
            label={variation.status}
            size="small"
            sx={{
              backgroundColor: variation.status === 'pending' ? '#f59e0b' :
                variation.status === 'applied' || variation.status === 'inserted' ? '#22c55e' : '#dc3545',
              color: '#fff', fontSize: '0.7rem', fontWeight: 'bold',
            }}
          />
          {isPending && !editing && canUpdate && (
            <>
              <Tooltip title="Edit before applying">
                <IconButton size="small" onClick={handleStartEdit}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {!editing ? (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', display: 'block', mb: 0.5 }}>
              Question:
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: questionText }} style={{ fontSize: 13, lineHeight: 1.6 }} />
          </Box>

          {/* Show options for generated variations */}
          {mode === 'generate' && variation.generatedOptions && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', display: 'block', mb: 0.5 }}>
                Options:
              </Typography>
              {(() => {
                try {
                  const opts = typeof variation.generatedOptions === 'string'
                    ? JSON.parse(variation.generatedOptions) : variation.generatedOptions;
                  return opts.map((opt, idx) => (
                    <Typography key={idx} variant="body2" sx={{
                      fontWeight: opt.correct ? 'bold' : 'normal',
                      color: opt.correct ? '#22c55e' : 'inherit',
                      fontSize: 13, ml: 1,
                    }}>
                      {String.fromCharCode(65 + idx)}) <span dangerouslySetInnerHTML={{ __html: opt.answer }} />
                      {opt.correct && ' ✓'}
                    </Typography>
                  ));
                } catch { return null; }
              })()}
            </Box>
          )}

          {explanationText && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', display: 'block', mb: 0.5 }}>
                Explanation:
              </Typography>
              <div dangerouslySetInnerHTML={{ __html: explanationText }}
                style={{ fontSize: 12, lineHeight: 1.5, color: '#555', maxHeight: 120, overflow: 'auto' }} />
            </Box>
          )}

          {isPending && (canApply || canReject || canDelete) && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
              {canApply && (
                <Button size="small" variant="contained" color="success"
                  startIcon={applying ? <CircularProgress size={14} color="inherit" /> :
                    (mode === 'restyle' ? <CheckCircleIcon /> : <AddCircleIcon />)}
                  disabled={applying}
                  onClick={() => onApply(variation.id)}
                  sx={{ textTransform: 'none' }}>
                  {mode === 'restyle' ? 'Apply to Question' : 'Insert as New Question'}
                </Button>
              )}
              {canReject && (
                <Button size="small" variant="outlined" color="error"
                  onClick={() => onReject(variation.id)}
                  disabled={applying}
                  sx={{ textTransform: 'none' }}>
                  Reject
                </Button>
              )}
              {canDelete && (
                <Button size="small" variant="text" color="error"
                  onClick={() => onDelete(variation.id)}
                  disabled={applying}
                  sx={{ textTransform: 'none', fontSize: 11 }}>
                  Delete
                </Button>
              )}
            </Box>
          )}
        </>
      ) : (
        <>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', mb: 0.5, display: 'block' }}>
            Edit Question:
          </Typography>
          <JoditEditor ref={questionEditorRef} value={editQuestion} config={joditConfig}
            onBlur={(e) => setEditQuestion(typeof e === 'string' ? e : e?.target?.innerHTML || '')} />

          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#888', mt: 2, mb: 0.5, display: 'block' }}>
            Edit Explanation:
          </Typography>
          <JoditEditor ref={explanationEditorRef} value={editExplanation} config={joditConfig}
            onBlur={(e) => setEditExplanation(typeof e === 'string' ? e : e?.target?.innerHTML || '')} />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
            <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveEdit} disabled={saving}
              sx={{ textTransform: 'none', backgroundColor: '#22c55e' }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="small" variant="outlined" startIcon={<CancelIcon />}
              onClick={() => setEditing(false)} disabled={saving}
              sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}

// ─── Style Tag Group ───────────────────────────────────────
function StyleTagGroup({ styleTag, variations, mode, onApply, onEdit, onReject, onDelete, applying }) {
  const tagConfig = STYLE_TAGS.find(t => t.key === styleTag) || { label: styleTag, color: '#666' };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Chip label={tagConfig.label} size="small"
          sx={{ backgroundColor: tagConfig.color, color: '#fff', fontWeight: 'bold' }} />
        <Typography variant="caption" sx={{ color: '#888' }}>
          {variations.length} variation{variations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
      {variations.map(v => (
        <VariationCard key={v.id} variation={v} mode={mode}
          onApply={onApply} onEdit={onEdit} onReject={onReject} onDelete={onDelete}
          applying={applying} />
      ))}
    </Box>
  );
}

// ─── Main Dialog Component ─────────────────────────────────
export default function QuestionVariationsDialog({ open, onClose, questionId, questionText, onQuestionUpdated }) {
  const canRestyle = usePermission('QA Actions', 'Restyle Questions');
  const canGenerate = usePermission('QA Actions', 'Generate Variations');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [restyleVariations, setRestyleVariations] = useState([]);
  const [generateVariations, setGenerateVariations] = useState([]);
  const [applying, setApplying] = useState(false);
  const canTriggerGenerate = activeTab === 0 ? canRestyle : canGenerate;

  const toggleStyle = (key) => {
    setSelectedStyles(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => setSelectedStyles(STYLE_TAGS.map(t => t.key));
  const clearAll = () => setSelectedStyles([]);

  // ─── Generate ────────────────────────────────────────────
  const handleGenerate = async () => {
    if (selectedStyles.length === 0) {
      toast.warn('Please select at least one style');
      return;
    }
    setGenerating(true);
    const headers = getHeaders();

    try {
      const isRestyle = activeTab === 0;
      const url = isRestyle
        ? `${apiPaths.questionVariationRestyleGenerate}/${questionId}`
        : `${apiPaths.questionVariationGenerateNew}/${questionId}`;

      const res = await postInstituteAppData(url, {
        data: { styleTags: selectedStyles },
        headers,
      });

      if (res?.success) {
        if (isRestyle) {
          setRestyleVariations(res.variations || []);
        } else {
          setGenerateVariations(res.variations || []);
        }
        toast.success(res.message || 'Variations generated');
      } else {
        toast.error(res?.message || 'Generation failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ─── Fetch existing ──────────────────────────────────────
  const fetchExisting = useCallback(async (tab) => {
    const headers = getHeaders();
    try {
      if (tab === 0) {
        const res = await getInstutueApp(
          `${apiPaths.questionVariationRestyleGet}/${questionId}`, null, headers);
        if (res?.success) setRestyleVariations(res.variations || []);
      } else {
        const res = await getInstutueApp(
          `${apiPaths.questionVariationGenerateGet}/${questionId}`, null, headers);
        if (res?.success) setGenerateVariations(res.variations || []);
      }
    } catch (e) { /* silent */ }
  }, [questionId]);

  // Fetch on tab change
  const handleTabChange = (e, newVal) => {
    setActiveTab(newVal);
    fetchExisting(newVal);
  };

  // Fetch on open
  React.useEffect(() => {
    if (open && questionId) {
      fetchExisting(0);
      fetchExisting(1);
    }
  }, [open, questionId, fetchExisting]);

  // ─── Apply / Insert ──────────────────────────────────────
  const handleApplyRestyle = async (restyledId) => {
    if (!window.confirm('This will update the question stem and explanation. The original will be saved in the change log. Continue?')) return;
    setApplying(true);
    try {
      const headers = getHeaders();
      const res = await postInstituteAppData(
        `${apiPaths.questionVariationRestyleApply}/${restyledId}/apply`,
        { data: {}, headers });
      if (res?.success) {
        toast.success('Question restyled successfully');
        fetchExisting(0);
        if (onQuestionUpdated) onQuestionUpdated();
      } else {
        toast.error(res?.message || 'Apply failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleInsertGenerated = async (generatedId) => {
    if (!window.confirm('This will create a new question in the question bank. Continue?')) return;
    setApplying(true);
    try {
      const headers = getHeaders();
      const res = await postInstituteAppData(
        `${apiPaths.questionVariationGenerateInsert}/${generatedId}/insert`,
        { data: {}, headers });
      if (res?.success) {
        toast.success(`New question #${res.newQuestionId} created`);
        fetchExisting(1);
      } else {
        toast.error(res?.message || 'Insert failed');
      }
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Insert failed');
    } finally {
      setApplying(false);
    }
  };

  // ─── Edit ────────────────────────────────────────────────
  const handleEditRestyle = async (id, question, explanation) => {
    const headers = getHeaders();
    await putInstituteAppData(`${apiPaths.questionVariationRestyleUpdate}/${id}`, {
      data: { restyledQuestion: question, restyledExplanation: explanation },
      headers,
    });
  };

  const handleEditGenerate = async (id, question, explanation) => {
    const headers = getHeaders();
    await putInstituteAppData(`${apiPaths.questionVariationGenerateUpdate}/${id}`, {
      data: { generatedQuestion: question, generatedExplanation: explanation },
      headers,
    });
  };

  // ─── Reject / Delete ────────────────────────────────────
  const handleReject = async (id, isRestyle) => {
    const headers = getHeaders();
    const url = isRestyle
      ? `${apiPaths.questionVariationRestyleReject}/${id}/reject`
      : `${apiPaths.questionVariationGenerateReject}/${id}/reject`;
    try {
      await patchInstituteAppData(url, { headers });
      toast.success('Variation rejected');
      fetchExisting(isRestyle ? 0 : 1);
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  const handleDelete = async (id, isRestyle) => {
    if (!window.confirm('Delete this variation?')) return;
    const headers = getHeaders();
    const url = isRestyle
      ? `${apiPaths.questionVariationRestyleDelete}/${id}`
      : `${apiPaths.questionVariationGenerateDelete}/${id}`;
    try {
      await deleteInstituteAppData(url, { headers });
      toast.success('Variation deleted');
      fetchExisting(isRestyle ? 0 : 1);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ─── Group variations by style tag ───────────────────────
  const groupByStyle = (variations) => {
    const groups = {};
    for (const v of variations) {
      if (!groups[v.styleTag]) groups[v.styleTag] = [];
      groups[v.styleTag].push(v);
    }
    return groups;
  };

  const restyleGroups = groupByStyle(restyleVariations);
  const generateGroups = groupByStyle(generateVariations);

  return (
    // disableEnforceFocus: the Jodit editors below render their image/video/link
    // popups into document.body, outside this dialog. MUI's FocusTrap would pull
    // focus back into the dialog on focusin, making those popups' URL inputs
    // impossible to type into.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth disableEnforceFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#6366f1' }} />
          <Typography variant="h6">AI Question Variations — Q#{questionId}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 500 }}>
        {/* Original Question Preview */}
        <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>ORIGINAL QUESTION</Typography>
          <div dangerouslySetInnerHTML={{ __html: questionText }} style={{ fontSize: 13, marginTop: 4 }} />
        </Paper>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tab label="Restyle Question" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
          <Tab label="Generate New Question" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
        </Tabs>

        {/* Style tag selector (shared) */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
              {activeTab === 0
                ? 'Select styles to restyle the question stem (answers stay the same):'
                : 'Select styles to generate entirely new questions (new stem + options):'}
            </Typography>
            <Box>
              <Button size="small" onClick={selectAll} sx={{ textTransform: 'none', fontSize: 11 }}>Select All</Button>
              <Button size="small" onClick={clearAll} sx={{ textTransform: 'none', fontSize: 11 }}>Clear</Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {STYLE_TAGS.map(tag => (
              <Chip key={tag.key} label={tag.label} clickable
                onClick={() => toggleStyle(tag.key)}
                variant={selectedStyles.includes(tag.key) ? 'filled' : 'outlined'}
                sx={{
                  backgroundColor: selectedStyles.includes(tag.key) ? tag.color : 'transparent',
                  color: selectedStyles.includes(tag.key) ? '#fff' : tag.color,
                  borderColor: tag.color,
                  fontWeight: selectedStyles.includes(tag.key) ? 'bold' : 'normal',
                  '&:hover': { backgroundColor: tag.color, color: '#fff', opacity: 0.85 },
                }}
              />
            ))}
          </Box>
        </Box>

        {canTriggerGenerate && (
          <Button variant="contained" startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleGenerate} disabled={generating || selectedStyles.length === 0}
            sx={{ mb: 2, textTransform: 'none', backgroundColor: '#6366f1' }}>
            {generating ? 'Generating...' : `Generate ${selectedStyles.length} Style${selectedStyles.length !== 1 ? 's' : ''} × 3 Variations`}
          </Button>
        )}

        {generating && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Generating variations for {selectedStyles.length} style(s). This may take 15-30 seconds per style.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Restyle Results */}
        <TabPanel value={activeTab} index={0}>
          {Object.keys(restyleGroups).length === 0 && !generating && (
            <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 4 }}>
              No restyled variations yet. Select styles above and click Generate.
            </Typography>
          )}
          {Object.entries(restyleGroups).map(([tag, variations]) => (
            <StyleTagGroup key={tag} styleTag={tag} variations={variations} mode="restyle"
              onApply={handleApplyRestyle}
              onEdit={handleEditRestyle}
              onReject={(id) => handleReject(id, true)}
              onDelete={(id) => handleDelete(id, true)}
              applying={applying} />
          ))}
        </TabPanel>

        {/* Generate Results */}
        <TabPanel value={activeTab} index={1}>
          {Object.keys(generateGroups).length === 0 && !generating && (
            <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 4 }}>
              No generated variations yet. Select styles above and click Generate.
            </Typography>
          )}
          {Object.entries(generateGroups).map(([tag, variations]) => (
            <StyleTagGroup key={tag} styleTag={tag} variations={variations} mode="generate"
              onApply={handleInsertGenerated}
              onEdit={handleEditGenerate}
              onReject={(id) => handleReject(id, false)}
              onDelete={(id) => handleDelete(id, false)}
              applying={applying} />
          ))}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
